import validateInitialization from "../../../core/js/helpers/validate-initialization";
import validateRequiredElements from "../../../core/js/helpers/validate-required-elements";
import {
    componentName,
    componentClasses,
    eventMap,
    defaultProps,
    views,
} from "../../../core/components/file-input/file-input";
import { getScreenResizeInstance } from "../../../core/js/state-machine/global-resizer";
import TooltipTemplate from "../tooltip/tooltip.template";
import { dispatchEvent } from "../../../core/js/state-machine";
import { actions } from "../../../core/js/state-machine/reducers/file-input";
import Tooltip from "../tooltip/tooltip";

const validateHTML = (element) => {
    const requiredElements = {
        feedbackElement: {
            query: `.${componentClasses.item.feedback}`,
            selector: element.querySelector(`.${componentClasses.item.feedback}`),
        },
        button: {
            query: `.${componentClasses.item.close}`,
            selector: element.querySelector(`.${componentClasses.item.close}`),
        },
        fileNameElement: {
            query: `.${componentClasses.item.name}[id]`,
            selector: element.querySelector(`.${componentClasses.item.name}[id]`),
        },
        contentElement: {
            query: `.${componentClasses.item.content}`,
            selector: element.querySelector(`.${componentClasses.item.content}`),
        },
        fileSizeElement: {
            query: `.${componentClasses.item.size}`,
            selector: element.querySelector(`.${componentClasses.item.size}`),
        },
    };

    const customReport = {
        fileNameElement: `${componentName} error validating required element. Missing element with class .${componentClasses.item.name} and an id`,
    };

    const [success, elements] = validateRequiredElements({
        requiredElements,
        componentName,
        customReport,
    });

    return success ? elements : false;
};

/**
 * Initialize the FileInputItem component
 * Used for managing individual file items for the file input component.
 * @param {HTMLElement} element file input list item element.
 * @param {defaultProps} props optional settings.
 * @returns {{
 *  element: HTMLElement,
 *  dispose: Function,
 *  id: unique file id,
 *  file: File object,
 *  remove: Function,
 *  setFeedback: Function,
 * }} public api object
 */

const FileInputItem = (element, props = {}) => {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        componentClasses,
        element,
        defaultProps,
        props,
        validateHTML,
        views,
    });
    if (!success) return;

    let tooltipTriggerElement;

    const ellipsisWidth = 13;

    // Grab the id from the span containing the name.  Use it to connect to it's associated tooltip.
    const fileNameId = validElements.fileNameElement.id;

    /* We need to announce to users who are using a screen reader that a file has been removed when the 
       remove icon has been clicked.  In order to do this, we must grab the ul which contains our sr-only
       div and update the innerHTML of it with the file name.
    */
    const ulElement = element.parentNode;
    const srElement = ulElement.querySelector(`.${componentClasses.srLabel}`);

    /**
     * When we want to remove the item from the list
     */
    const handleRemove = () => {
        mergedProps.fileInputStore.dispatch(actions.removeListItem(mergedProps.id));
        dispatchEvent(eventMap.itemDeleted, element);

        // If the list no longer contains files with errors on them, update the state of fieldError to false
        checkFileSizeAndCount();

        // Announce here that a certain file has been removed.
        srElement.innerHTML = mergedProps.srFileRemovedDescription.replace(
            "{fileName}",
            mergedProps.file.name
        );
        dispose();
    };

    /**
     * Whenever our component state changes, we want to react to it.
     * @param {object} newState - reducer state object
     */
    const handleStateChange = (newState) => {
        // We only care about our items state.
        const newItemState = newState.list.find((item) => item.id === mergedProps.id);

        if (!newItemState) {
            // Oh no we've been removed!
            return;
        }

        // We always want to be updating the feedback
        validElements.feedbackElement.innerHTML = newItemState.error || "";
    };

    const dispose = () => {
        validElements.button.removeEventListener("click", handleRemove);
        element.remove();
        element = null;
    };

    /**
     * This function takes the 2 truncated (or not truncated) strings and combines them.  If truncated, it adds
     * the ellipsis inbetween the strings.
     * @param {*} usableSpace
     * @param {*} firstHalfString
     * @param {*} secondHalfReversedString
     * @param {*} halfSpace
     * @param {*} fileName
     * @returns
     */
    const unifyStrings = (
        usableSpace,
        firstHalfString,
        secondHalfReversedString,
        halfSpace,
        fileName
    ) => {
        let unifiedStrings;

        const startString = createFirstHalfTruncString(firstHalfString, halfSpace, fileName);

        const endString = createSecondHalfTruncString(
            secondHalfReversedString,
            halfSpace,
            fileName
        );

        const totalUsedSpace = startString.firstHalfUsedSpace + endString.secondHalfUsedSpace;

        if (totalUsedSpace <= usableSpace) {
            unifiedStrings = startString.firstTruncString.concat(endString.secondTruncString);
            return unifiedStrings;
        }
        //If the element does not have a tooltip, create one.
        if (!tooltipTriggerElement) {
            tooltipTriggerElement = new DOMParser().parseFromString(
                TooltipTemplate({
                    id: mergedProps.id,
                    trigger: fileNameId,
                    body: mergedProps.file.name,
                }),
                "text/html"
            ).body.firstChild;

            element.appendChild(tooltipTriggerElement);

            unifiedStrings = startString.firstTruncString.concat(
                "...",
                endString.secondTruncString
            );

            // Once tooltip html has been created and attached to the element, initialize it.
            element.querySelectorAll('[data-dds="tooltip"]').forEach((tooltip) => {
                Tooltip(tooltip);
            });

            return unifiedStrings;
        }
        unifiedStrings = startString.firstTruncString.concat("...", endString.secondTruncString);
        return unifiedStrings;
    };

    /**
     * Handles initial and all re-renders.
     */
    const render = () => {
        // If this is a re-render, need to remove the file name from being displayed so it does not wrap if too long.
        // If it is allowed to wrap, it will throw off the offsetWidth calculation.
        validElements.fileNameElement.innerHTML = "";

        const fileName = mergedProps.file.name;
        const totalSpace = validElements.contentElement.offsetWidth;
        const fileSizeElementWidth = validElements.fileSizeElement.offsetWidth;
        const usableSpace = totalSpace - fileSizeElementWidth - ellipsisWidth;
        const halfSpace = Math.floor(usableSpace / 2);

        const firstHalfString = fileName.substring(0, Math.ceil(fileName.length / 2));
        const secondHalfString = fileName.substring(Math.ceil(fileName.length / 2));

        // Combine into a func that takes in an array and returns a reversed string.
        const secondHalfArray = Array.from(secondHalfString);
        const secondHalfReversedString = secondHalfArray.reverse().join("");

        validElements.fileNameElement.innerHTML = unifyStrings(
            usableSpace,
            firstHalfString,
            secondHalfReversedString,
            halfSpace,
            validElements.fileNameElement
        );
    };

    const setFeedback = (message) => {
        validElements.feedbackElement.innerHTML = message;
    };

    const handleResize = () => {
        render();
    };

    const checkFileSizeAndCount = () => {
        if (
            mergedProps.fileInputStore.getState().fullItemCount ===
            mergedProps.fileInputStore.getState().validItemCount
        ) {
            mergedProps.fileInputStore.dispatch(actions.setFieldError(false));
        }
    };

    // Init
    mergedProps.fileInputStore.subscribe(handleStateChange);

    render();
    validElements.button.addEventListener("click", handleRemove);
    // Use Global Resizer instance to watch for width changes and retruncate the file name according to available width.
    getScreenResizeInstance().getState();
    getScreenResizeInstance().subscribe(handleResize);

    const api = {
        element,
        dispose,
        id: mergedProps.id,
        file: mergedProps.file,
        remove: handleRemove,
        setFeedback,
    };

    return api;
};

export default FileInputItem;

/**
 *
 * @param {String} string
 * @param {Number} halfAvailableSpace
 * @param {*HTMLElement} textElement
 * @returns {Object} firstTruncString, firstHalfUsedSpace
 */

const createFirstHalfTruncString = (string, halfAvailableSpace, textElement) => {
    let firstHalfUsedSpace = 0;
    let firstTruncString = "";

    for (let i = 0; i <= string.length; i++) {
        if (firstHalfUsedSpace < halfAvailableSpace) {
            firstTruncString = firstTruncString.concat(string.charAt(i));
            textElement.innerHTML = firstTruncString;
            firstHalfUsedSpace = textElement.offsetWidth;
        } else if (firstHalfUsedSpace > halfAvailableSpace) {
            // If the space taken by the text is larger than the available space, then remove a letter.
            firstTruncString = firstTruncString.slice(0, -1);
            return { firstTruncString, firstHalfUsedSpace };
        }
    }

    return { firstTruncString, firstHalfUsedSpace };
};

/**
 * This function is different from it's sibling, createSecondHalfTruncString in that the string it receives is reversed and
 * it needs to reverse the string  back to original format before returning it.
 * @param {String} string
 * @param {Number} halfAvailableSpace
 * @param {*HTMLElement} textElement
 * @returns {Object} firstTruncString, firstHalfUsedSpace
 */
const createSecondHalfTruncString = (string, halfAvailableSpace, textElement) => {
    let secondHalfUsedSpace = 0;
    let updatedReverseString = "";
    let secondTruncString;

    for (let i = 0; i <= string.length; i++) {
        if (secondHalfUsedSpace < halfAvailableSpace) {
            updatedReverseString = updatedReverseString.concat(string.charAt(i));
            textElement.innerHTML = updatedReverseString;
            secondHalfUsedSpace = textElement.offsetWidth;
        } else if (secondHalfUsedSpace > halfAvailableSpace) {
            // Lop off the last 2 characters, turn into an array and flip it and then turn into a string.
            updatedReverseString = updatedReverseString.slice(0, -1);
            secondTruncString = Array.from(updatedReverseString).reverse().join("");
            return { secondTruncString, secondHalfUsedSpace };
        }
    }
    secondTruncString = Array.from(updatedReverseString).reverse().join("");

    return { secondTruncString, secondHalfUsedSpace };
};
